import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { HelpCircleIcon } from "@/components/icons";
import {
  Button,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLayout,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  useFieldError,
} from "@/components/kit";
import {
  parseConversionFormula,
  previewUnitConversion,
} from "@/modules/products/domain/parse-conversion-formula";

type Props = {
  name: string;
  label: string;
  stockUnitField: string;
  receiveUnitField: string;
  targetUnitHint: string;
  placeholder?: string;
};

export function ConversionFactorField({
  name,
  label,
  stockUnitField,
  receiveUnitField,
  targetUnitHint,
  placeholder,
}: Props) {
  const { register, watch, setValue } = useFormContext();
  const error = useFieldError(name);
  const stockUnitLabel = watch(stockUnitField) as string | undefined;
  const receiveUnitLabel = watch(receiveUnitField) as string | null | undefined;
  const factorValue = watch(name) as number | string | undefined;

  const [formulaInput, setFormulaInput] = useState("");
  const [sampleReceiveQty, setSampleReceiveQty] = useState("1");
  const [open, setOpen] = useState(false);

  const parsedFormula = useMemo(
    () => (formulaInput.trim() ? parseConversionFormula(formulaInput) : null),
    [formulaInput],
  );

  const previewFactor =
    parsedFormula?.ok === true
      ? parsedFormula.factor
      : Number(factorValue) > 0
        ? Number(factorValue)
        : undefined;

  const preview = previewUnitConversion({
    receiveQty: Number(sampleReceiveQty) || 0,
    factor: previewFactor,
    receiveUnitLabel,
    stockUnitLabel,
  });

  useEffect(() => {
    if (!open) return;
    if (factorValue != null && factorValue !== "" && !formulaInput) {
      setFormulaInput(String(factorValue));
    }
  }, [open, factorValue, formulaInput]);

  function applyFormula() {
    if (!parsedFormula?.ok) return;
    setValue(name, parsedFormula.factor, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setOpen(false);
  }

  const stockUnit = preview.stockUnit;
  const receiveUnit = preview.receiveUnit;

  return (
    <FieldLayout data-invalid={error ? true : undefined}>
      <div className="flex items-center gap-1.5">
        <FieldLabel htmlFor={name}>{label}</FieldLabel>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-6 text-muted-foreground"
              aria-label="ຊ່ວຍເຫຼືອອັດຕາແປງ"
            >
              <HelpCircleIcon className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 sm:w-96" align="start">
            <div className="space-y-3">
              <div>
                <p className="font-medium text-sm">ສູດຄຳນວນ</p>
                <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
                  {targetUnitHint} = ຈຳນວນຮັບເຂົ້າ × ອັດຕາແປງ
                </p>
                <p className="mt-2 rounded-lg bg-muted/50 px-3 py-2 font-mono text-xs">
                  1 {receiveUnit} = {previewFactor ? preview.factor : "?"}{" "}
                  {stockUnit}
                </p>
              </div>

              <Separator />

              <div className="space-y-1.5">
                <FieldLabel htmlFor={`${name}-formula`}>ລອງໃສ່ສູດ</FieldLabel>
                <Input
                  id={`${name}-formula`}
                  value={formulaInput}
                  onChange={(e) => setFormulaInput(e.target.value)}
                  placeholder="100 ຫຼື 1=100 ຫຼື 1 ມັດ = 100 ຊິ້ນ"
                />
                {parsedFormula?.ok === false ? (
                  <FieldDescription className="text-destructive">
                    {parsedFormula.error}
                  </FieldDescription>
                ) : parsedFormula?.ok ? (
                  <FieldDescription>
                    ໄດ້ factor ={" "}
                    {parsedFormula.factor.toLocaleString("lo-LA", {
                      maximumFractionDigits: 4,
                    })}
                  </FieldDescription>
                ) : (
                  <FieldDescription>
                    ພິມຕົວເລກ ຫຼື ສູດແບບ 1=100 ເພື່ອ preview
                  </FieldDescription>
                )}
              </div>

              <div className="space-y-1.5">
                <FieldLabel htmlFor={`${name}-sample-qty`}>
                  ລອງຮັບເຂົ້າ (preview)
                </FieldLabel>
                <Input
                  id={`${name}-sample-qty`}
                  type="number"
                  min={0}
                  step="any"
                  value={sampleReceiveQty}
                  onChange={(e) => setSampleReceiveQty(e.target.value)}
                />
              </div>

              <div className="rounded-xl border border-secondary/20 bg-background/70 px-3 py-2">
                <p className="text-muted-foreground text-xs">ຜົນລັບ preview</p>
                <p className="mt-1 font-medium text-sm">{preview.summary}</p>
              </div>

              <Button
                type="button"
                className="w-full rounded-full"
                disabled={parsedFormula?.ok !== true}
                onClick={applyFormula}
              >
                ໃຊ້ອັດຕາແປງນີ້
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <FieldContent>
        <Input
          id={name}
          type="number"
          min={0.0001}
          step="any"
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          {...register(name)}
        />
        {error ? (
          <FieldError>{error}</FieldError>
        ) : (
          <FieldDescription>
            1 {receiveUnit} = {previewFactor ? preview.factor : "…"} {stockUnit}
          </FieldDescription>
        )}
      </FieldContent>
    </FieldLayout>
  );
}
